import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

const APARELHO_KEY = ['me', 'push', 'aparelho'] as const;

/** Configuração pública do push (chave VAPID + se está ativo no servidor). */
export interface PushPublicKey {
  publicKey: string;
  enabled: boolean;
}

/** Este navegador/aparelho suporta notificações push? */
export function pushSuportado(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Chave pública VAPID do servidor (o navegador precisa dela para se inscrever). */
export function usePushPublicKey() {
  return useQuery({
    queryKey: ['me', 'push', 'public-key'],
    queryFn: async () => {
      const { data } = await apiClient.get<PushPublicKey>('/v1/me/push/public-key');
      return data;
    },
    staleTime: Infinity,
    enabled: pushSuportado(),
  });
}

/** Converte a chave VAPID (Base64 URL-safe) no formato exigido pelo PushManager. */
function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const output = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

/** Inscrição push deste aparelho, se existir (lida do próprio navegador). */
export async function obterInscricaoLocal(): Promise<PushSubscription | null> {
  if (!pushSuportado()) {
    return null;
  }
  // getRegistration, e não `ready`: sem service worker registrado (em desenvolvimento não há),
  // `ready` nunca termina e a tela - ou o "Sair" - ficaria esperando para sempre.
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
}

/** Registra a inscrição deste aparelho no servidor, em nome de quem está logado. */
async function registrarNoServidor(sub: PushSubscription) {
  const json = sub.toJSON();
  await apiClient.post('/v1/me/push/subscribe', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
  });
}

/**
 * Este aparelho recebe os avisos de quem está logado?
 *
 * A inscrição é do navegador, não da pessoa: quem ligou os avisos pode ter saído e outra pessoa
 * entrado no mesmo navegador. Por isso a inscrição local é reenviada ao servidor, que a passa
 * para quem está logado - só então o aparelho conta como ligado. Se o servidor não confirmar, o
 * resultado é erro (a tela diz que não deu para confirmar), nunca "ligado".
 *
 * Roda ao entrar no app (AppLayout). Sai da memória quando a pessoa deixa as telas do app (Sair,
 * sessão expirada), para o próximo login conferir de novo.
 */
export function useAvisosDoAparelho() {
  return useQuery({
    queryKey: APARELHO_KEY,
    queryFn: async () => {
      const sub = await obterInscricaoLocal();
      if (!sub) {
        return false;
      }
      await registrarNoServidor(sub);
      return true;
    },
    enabled: pushSuportado(),
    staleTime: Infinity,
    gcTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/**
 * "Sair": o servidor para de mandar avisos para este aparelho, mas a inscrição do navegador
 * fica - no próximo login {@link useAvisosDoAparelho} a religa para quem entrar. Assim o
 * computador compartilhado não mostra os processos de quem saiu, e no computador pessoal
 * ninguém precisa ativar de novo. Nunca falha e espera no máximo 3 s: não pode prender a saída.
 */
export async function pausarAvisosDoAparelho(): Promise<void> {
  try {
    const sub = await obterInscricaoLocal();
    if (sub) {
      await apiClient.delete('/v1/me/push/subscribe', {
        data: { endpoint: sub.endpoint },
        timeout: 3000,
      });
    }
  } catch {
    // Sem internet ou sessão já expirada: sai assim mesmo. O próximo login troca o dono.
  }
}

/**
 * Liga os avisos neste aparelho: pede permissão, inscreve no navegador e registra no
 * servidor. Lança erro com mensagem específica quando a permissão é negada.
 */
export function useAtivarPush() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vapidPublicKey: string) => {
      const permissao = await Notification.requestPermission();
      if (permissao !== 'granted') {
        throw new Error('PERMISSAO_NEGADA');
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
      await registrarNoServidor(sub);
    },
    onSuccess: () => queryClient.setQueryData(APARELHO_KEY, true),
  });
}

/** Desliga os avisos neste aparelho: remove no servidor e cancela a inscrição no navegador. */
export function useDesativarPush() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const sub = await obterInscricaoLocal();
      if (!sub) {
        return;
      }
      await apiClient
        .delete('/v1/me/push/subscribe', { data: { endpoint: sub.endpoint } })
        .catch(() => undefined);
      await sub.unsubscribe();
    },
    onSuccess: () => queryClient.setQueryData(APARELHO_KEY, false),
  });
}

/** Envia um aviso de teste para os aparelhos inscritos do usuário. */
export function useTestarPush() {
  return useMutation({
    mutationFn: async () => {
      await apiClient.post('/v1/me/push/test');
    },
  });
}
